/**
 * Error Reporting Utilities
 *
 * Provides error classification, user-friendly messages, recovery strategies,
 * and an extensible error reporting interface (Sentry-compatible).
 *
 * @module utils/errorReporting
 */

import { logger } from './logger';

// ============================================================================
// Error Types
// ============================================================================

/**
 * Classification of application errors by their source/nature
 */
export const ErrorType = {
  /** Error during text analysis (parsing, scoring, etc.) */
  ANALYSIS_ERROR: 'analysis_error',
  /** Error from Web Worker communication or execution */
  WORKER_ERROR: 'worker_error',
  /** Network/fetch related errors */
  NETWORK_ERROR: 'network_error',
  /** Input validation failures */
  VALIDATION_ERROR: 'validation_error',
  /** Catch-all for unclassified errors */
  UNKNOWN_ERROR: 'unknown_error',
  /** Model loading/inference errors */
  MODEL_ERROR: 'model_error',
  /** Storage/database errors (IndexedDB, localStorage) */
  STORAGE_ERROR: 'storage_error',
  /** Rendering/component errors */
  RENDER_ERROR: 'render_error',
} as const;

export type ErrorType = typeof ErrorType[keyof typeof ErrorType];

/**
 * Structured application error with metadata
 */
export interface AppError {
  /** Error classification */
  type: ErrorType;
  /** Human-readable error message */
  message: string;
  /** Original error object if available */
  originalError?: Error;
  /** Additional context for debugging */
  context?: Record<string, unknown>;
  /** Timestamp when error occurred */
  timestamp: number;
  /** Whether the error can be recovered from */
  recoverable: boolean;
  /** Stack trace if available */
  stack?: string;
  /** Unique error ID for tracking */
  id: string;
}

/**
 * Recovery strategy for handling errors
 */
export type RecoveryStrategy = 'retry' | 'reload' | 'reset' | 'contact_support';

// ============================================================================
// User-Friendly Error Messages
// ============================================================================

/**
 * User-friendly error messages by error type
 */
export const ERROR_MESSAGES: Record<ErrorType, string> = {
  [ErrorType.ANALYSIS_ERROR]:
    "We couldn't complete the analysis. The text might be too long or contain unsupported characters.",
  [ErrorType.WORKER_ERROR]:
    'The analysis worker encountered an issue. Please refresh the page.',
  [ErrorType.NETWORK_ERROR]:
    'Unable to load required resources. Please check your internet connection.',
  [ErrorType.VALIDATION_ERROR]:
    "The input doesn't appear to be valid text. Please try again.",
  [ErrorType.UNKNOWN_ERROR]:
    'An unexpected error occurred. Please try again or contact support.',
  [ErrorType.MODEL_ERROR]:
    'Failed to load or run the analysis model. Try refreshing or clearing browser data.',
  [ErrorType.STORAGE_ERROR]:
    'Unable to save or load data. Your browser storage might be full or restricted.',
  [ErrorType.RENDER_ERROR]:
    'A display error occurred. Please refresh the page.',
};

/**
 * Detailed error descriptions for technical users (shown in expandable details)
 */
export const ERROR_DETAILS: Record<ErrorType, string> = {
  [ErrorType.ANALYSIS_ERROR]:
    'The text analysis pipeline failed during execution. This can happen with very long texts, texts containing unusual Unicode characters, or when memory limits are exceeded.',
  [ErrorType.WORKER_ERROR]:
    'Communication with the background analysis worker failed. This may indicate the worker crashed or timed out.',
  [ErrorType.NETWORK_ERROR]:
    'A network request failed. This could be due to connectivity issues, CORS restrictions, or the requested resource being unavailable.',
  [ErrorType.VALIDATION_ERROR]:
    'Input validation failed. The provided data did not meet the expected format or constraints.',
  [ErrorType.UNKNOWN_ERROR]:
    'An error occurred that could not be classified. Check the browser console for more details.',
  [ErrorType.MODEL_ERROR]:
    'The machine learning model failed to initialize or run inference. This may be due to incompatible WebGL/WASM support or memory constraints.',
  [ErrorType.STORAGE_ERROR]:
    'Browser storage operation failed. This can happen when storage is full, in private browsing mode, or when storage is disabled.',
  [ErrorType.RENDER_ERROR]:
    'React encountered an error while rendering a component. This may be due to invalid props or state.',
};

// ============================================================================
// Error Classification
// ============================================================================

/**
 * Patterns used to classify errors by their message content
 */
const ERROR_PATTERNS: Array<{ pattern: RegExp; type: ErrorType }> = [
  // Worker errors
  { pattern: /worker/i, type: ErrorType.WORKER_ERROR },
  { pattern: /postMessage/i, type: ErrorType.WORKER_ERROR },
  { pattern: /terminated/i, type: ErrorType.WORKER_ERROR },

  // Network errors
  { pattern: /fetch/i, type: ErrorType.NETWORK_ERROR },
  { pattern: /network/i, type: ErrorType.NETWORK_ERROR },
  { pattern: /cors/i, type: ErrorType.NETWORK_ERROR },
  { pattern: /timeout/i, type: ErrorType.NETWORK_ERROR },
  { pattern: /ERR_NETWORK/i, type: ErrorType.NETWORK_ERROR },
  { pattern: /Failed to fetch/i, type: ErrorType.NETWORK_ERROR },

  // Analysis errors
  { pattern: /analysis/i, type: ErrorType.ANALYSIS_ERROR },
  { pattern: /parse/i, type: ErrorType.ANALYSIS_ERROR },
  { pattern: /tokenize/i, type: ErrorType.ANALYSIS_ERROR },
  { pattern: /sentence/i, type: ErrorType.ANALYSIS_ERROR },

  // Validation errors
  { pattern: /validation/i, type: ErrorType.VALIDATION_ERROR },
  { pattern: /invalid/i, type: ErrorType.VALIDATION_ERROR },
  { pattern: /required/i, type: ErrorType.VALIDATION_ERROR },
  { pattern: /expected/i, type: ErrorType.VALIDATION_ERROR },

  // Model errors
  { pattern: /model/i, type: ErrorType.MODEL_ERROR },
  { pattern: /tensorflow/i, type: ErrorType.MODEL_ERROR },
  { pattern: /webgl/i, type: ErrorType.MODEL_ERROR },
  { pattern: /wasm/i, type: ErrorType.MODEL_ERROR },
  { pattern: /onnx/i, type: ErrorType.MODEL_ERROR },

  // Storage errors
  { pattern: /indexeddb/i, type: ErrorType.STORAGE_ERROR },
  { pattern: /localstorage/i, type: ErrorType.STORAGE_ERROR },
  { pattern: /quota/i, type: ErrorType.STORAGE_ERROR },
  { pattern: /storage/i, type: ErrorType.STORAGE_ERROR },

  // Render errors
  { pattern: /render/i, type: ErrorType.RENDER_ERROR },
  { pattern: /component/i, type: ErrorType.RENDER_ERROR },
  { pattern: /react/i, type: ErrorType.RENDER_ERROR },
];

/**
 * Generate a unique error ID
 */
function generateErrorId(): string {
  return `err_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 8)}`;
}

/**
 * Classify an unknown error into a structured AppError
 *
 * @param error - The error to classify (can be any type)
 * @param contextHint - Optional hint about the context where the error occurred
 * @returns Structured AppError with classification
 */
export function classifyError(
  error: unknown,
  contextHint?: ErrorType
): AppError {
  const timestamp = Date.now();
  const id = generateErrorId();

  // Handle null/undefined
  if (error == null) {
    return {
      type: contextHint ?? ErrorType.UNKNOWN_ERROR,
      message: 'An unknown error occurred',
      timestamp,
      recoverable: true,
      id,
    };
  }

  // Handle Error objects
  if (error instanceof Error) {
    const errorMessage = error.message || error.toString();
    let type = contextHint ?? ErrorType.UNKNOWN_ERROR;

    // Try to classify by message pattern
    if (!contextHint) {
      for (const { pattern, type: errorType } of ERROR_PATTERNS) {
        if (pattern.test(errorMessage) || pattern.test(error.name)) {
          type = errorType;
          break;
        }
      }
    }

    return {
      type,
      message: errorMessage,
      originalError: error,
      timestamp,
      recoverable: isRecoverableType(type),
      stack: error.stack,
      id,
    };
  }

  // Handle string errors
  if (typeof error === 'string') {
    let type = contextHint ?? ErrorType.UNKNOWN_ERROR;

    if (!contextHint) {
      for (const { pattern, type: errorType } of ERROR_PATTERNS) {
        if (pattern.test(error)) {
          type = errorType;
          break;
        }
      }
    }

    return {
      type,
      message: error,
      timestamp,
      recoverable: isRecoverableType(type),
      id,
    };
  }

  // Handle objects with message property
  if (typeof error === 'object' && 'message' in error) {
    const message = String((error as { message: unknown }).message);
    return classifyError(message, contextHint);
  }

  // Fallback for other types
  return {
    type: contextHint ?? ErrorType.UNKNOWN_ERROR,
    message: String(error),
    timestamp,
    recoverable: true,
    id,
  };
}

/**
 * Check if an error type is typically recoverable
 */
function isRecoverableType(type: ErrorType): boolean {
  switch (type) {
    case ErrorType.VALIDATION_ERROR:
    case ErrorType.NETWORK_ERROR:
    case ErrorType.ANALYSIS_ERROR:
      return true;
    case ErrorType.WORKER_ERROR:
    case ErrorType.STORAGE_ERROR:
    case ErrorType.MODEL_ERROR:
    case ErrorType.RENDER_ERROR:
      return false;
    case ErrorType.UNKNOWN_ERROR:
    default:
      return true;
  }
}

/**
 * Check if an AppError is recoverable
 *
 * @param error - The AppError to check
 * @returns true if the error can be recovered from
 */
export function isRecoverable(error: AppError): boolean {
  return error.recoverable;
}

/**
 * Get user-friendly error message for an AppError
 *
 * @param error - The AppError to get message for
 * @returns User-friendly message string
 */
export function getErrorMessage(error: AppError): string {
  return ERROR_MESSAGES[error.type] || ERROR_MESSAGES[ErrorType.UNKNOWN_ERROR];
}

/**
 * Get detailed technical description for an error
 *
 * @param error - The AppError to get details for
 * @returns Technical details string
 */
export function getErrorDetails(error: AppError): string {
  return ERROR_DETAILS[error.type] || ERROR_DETAILS[ErrorType.UNKNOWN_ERROR];
}

/**
 * Determine the best recovery strategy for an error
 *
 * @param error - The AppError to get strategy for
 * @returns Recommended recovery strategy
 */
export function getRecoveryStrategy(error: AppError): RecoveryStrategy {
  switch (error.type) {
    case ErrorType.VALIDATION_ERROR:
      // User can fix input and retry
      return 'retry';

    case ErrorType.NETWORK_ERROR:
      // Network issues often resolve on retry
      return 'retry';

    case ErrorType.ANALYSIS_ERROR:
      // Reset analysis state and let user try again
      return 'reset';

    case ErrorType.WORKER_ERROR:
      // Worker crashes require page reload
      return 'reload';

    case ErrorType.MODEL_ERROR:
      // Model issues may require reload
      return 'reload';

    case ErrorType.STORAGE_ERROR:
      // Storage issues may need manual intervention
      return 'contact_support';

    case ErrorType.RENDER_ERROR:
      // Reset component state
      return 'reset';

    case ErrorType.UNKNOWN_ERROR:
    default:
      // Unknown errors - try retry first
      return 'retry';
  }
}

/**
 * Get action button text for a recovery strategy
 */
export function getRecoveryActionText(strategy: RecoveryStrategy): string {
  switch (strategy) {
    case 'retry':
      return 'Try Again';
    case 'reload':
      return 'Reload Page';
    case 'reset':
      return 'Reset';
    case 'contact_support':
      return 'Contact Support';
  }
}

// ============================================================================
// Error Reporter Interface
// ============================================================================

/**
 * Interface for error reporting services (Sentry-compatible)
 */
export interface ErrorReporter {
  /** Report an error */
  captureError(error: AppError): void;
  /** Report a message/event */
  captureMessage(message: string, context?: Record<string, unknown>): void;
  /** Set user context for error reports */
  setUser(userId: string, userData?: Record<string, unknown>): void;
  /** Clear user context */
  clearUser(): void;
  /** Add a breadcrumb for debugging */
  addBreadcrumb(
    message: string,
    category?: string,
    data?: Record<string, unknown>
  ): void;
  /** Set a tag for filtering */
  setTag(key: string, value: string): void;
  /** Set extra context */
  setExtra(key: string, value: unknown): void;
}

/**
 * Breadcrumb for error tracking
 */
interface Breadcrumb {
  timestamp: number;
  message: string;
  category?: string;
  data?: Record<string, unknown>;
}

/**
 * Maximum number of breadcrumbs to keep
 */
const MAX_BREADCRUMBS = 50;

/**
 * In-memory storage for breadcrumbs (used by console reporter)
 */
const breadcrumbs: Breadcrumb[] = [];

/**
 * Default console-based error reporter (for development)
 * Logs errors to console and stores breadcrumbs in memory
 */
class ConsoleErrorReporter implements ErrorReporter {
  private user: { id: string; data?: Record<string, unknown> } | null = null;
  private tags: Record<string, string> = {};
  private extras: Record<string, unknown> = {};

  captureError(error: AppError): void {
    const context = {
      ...error.context,
      user: this.user,
      tags: this.tags,
      extras: this.extras,
      breadcrumbs: [...breadcrumbs],
    };

    logger.critical('[Error Reporter] Error captured:', {
      id: error.id,
      type: error.type,
      message: error.message,
      recoverable: error.recoverable,
      timestamp: new Date(error.timestamp).toISOString(),
      context,
      stack: error.stack,
    });
  }

  captureMessage(message: string, context?: Record<string, unknown>): void {
    logger.info('[Error Reporter] Message:', message, {
      ...context,
      user: this.user,
      tags: this.tags,
    });
  }

  setUser(userId: string, userData?: Record<string, unknown>): void {
    this.user = { id: userId, data: userData };
    logger.log('[Error Reporter] User set:', userId);
  }

  clearUser(): void {
    this.user = null;
    logger.log('[Error Reporter] User cleared');
  }

  addBreadcrumb(
    message: string,
    category?: string,
    data?: Record<string, unknown>
  ): void {
    const breadcrumb: Breadcrumb = {
      timestamp: Date.now(),
      message,
      category,
      data,
    };

    breadcrumbs.push(breadcrumb);

    // Keep only the most recent breadcrumbs
    if (breadcrumbs.length > MAX_BREADCRUMBS) {
      breadcrumbs.shift();
    }

    logger.log('[Error Reporter] Breadcrumb:', message, category, data);
  }

  setTag(key: string, value: string): void {
    this.tags[key] = value;
  }

  setExtra(key: string, value: unknown): void {
    this.extras[key] = value;
  }
}

/**
 * Sentry-backed error reporter (for production with Sentry DSN)
 * Lazily imports Sentry to avoid increasing initial bundle size
 */
class SentryErrorReporter implements ErrorReporter {
  private tags: Record<string, string> = {};
  private extras: Record<string, unknown> = {};

  captureError(error: AppError): void {
    import('../lib/sentry').then(({ captureException }) => {
      captureException(error.originalError ?? new Error(error.message), {
        tags: { errorType: error.type, recoverable: String(error.recoverable), ...this.tags },
        extra: { ...error.context, ...this.extras },
      });
    });
  }

  captureMessage(message: string, context?: Record<string, unknown>): void {
    import('../lib/sentry').then(({ captureMessage }) => {
      captureMessage(message, {
        tags: this.tags,
        extra: { ...context, ...this.extras },
      });
    });
  }

  setUser(userId: string, userData?: Record<string, unknown>): void {
    import('../lib/sentry').then(({ setUser }) => {
      setUser({ id: userId, ...userData });
    });
  }

  clearUser(): void {
    import('../lib/sentry').then(({ setUser }) => {
      setUser(null);
    });
  }

  addBreadcrumb(
    message: string,
    category?: string,
    data?: Record<string, unknown>
  ): void {
    const breadcrumb: Breadcrumb = {
      timestamp: Date.now(),
      message,
      category,
      data,
    };

    breadcrumbs.push(breadcrumb);
    if (breadcrumbs.length > MAX_BREADCRUMBS) {
      breadcrumbs.shift();
    }
  }

  setTag(key: string, value: string): void {
    this.tags[key] = value;
    import('../lib/sentry').then(({ setTag }) => {
      setTag(key, value);
    });
  }

  setExtra(key: string, value: unknown): void {
    this.extras[key] = value;
    import('../lib/sentry').then(({ setContext }) => {
      setContext('extra', { ...this.extras });
    });
  }
}

/**
 * No-op error reporter (for production without external service)
 */
class NoopErrorReporter implements ErrorReporter {
  captureError(): void {
    // No-op
  }
  captureMessage(): void {
    // No-op
  }
  setUser(): void {
    // No-op
  }
  clearUser(): void {
    // No-op
  }
  addBreadcrumb(): void {
    // No-op
  }
  setTag(): void {
    // No-op
  }
  setExtra(): void {
    // No-op
  }
}

// ============================================================================
// Error Reporter Singleton
// ============================================================================

/**
 * Configuration for error reporting
 */
export interface ErrorReportingConfig {
  /** Sentry DSN (if using Sentry) */
  dsn?: string;
  /** Environment name */
  environment?: string;
  /** App version/release */
  release?: string;
  /** Enable console logging */
  enableConsole?: boolean;
  /** Sample rate for error capture (0-1) */
  sampleRate?: number;
}

/**
 * Active error reporter instance
 */
let activeReporter: ErrorReporter = new ConsoleErrorReporter();

/**
 * Get the active error reporter
 */
export const errorReporter: ErrorReporter = {
  captureError: (error) => activeReporter.captureError(error),
  captureMessage: (message, context) =>
    activeReporter.captureMessage(message, context),
  setUser: (userId, userData) => activeReporter.setUser(userId, userData),
  clearUser: () => activeReporter.clearUser(),
  addBreadcrumb: (message, category, data) =>
    activeReporter.addBreadcrumb(message, category, data),
  setTag: (key, value) => activeReporter.setTag(key, value),
  setExtra: (key, value) => activeReporter.setExtra(key, value),
};

/**
 * Initialize error reporting
 *
 * In development: Uses console-based reporter
 * In production: Can be configured with Sentry DSN
 *
 * @param config - Configuration options
 */
export function initErrorReporting(config?: ErrorReportingConfig): void {
  const isDev =
    typeof process !== 'undefined'
      ? process.env.NODE_ENV === 'development'
      : import.meta.env?.DEV ?? false;

  // Set environment tag
  const environment = config?.environment ?? (isDev ? 'development' : 'production');

  if (config?.dsn) {
    activeReporter = new SentryErrorReporter();
    logger.info('[Error Reporting] Using Sentry reporter');
  } else if (isDev || config?.enableConsole) {
    // Use console reporter in development
    activeReporter = new ConsoleErrorReporter();
    logger.info('[Error Reporting] Using console reporter');
  } else {
    // Use no-op reporter in production without config
    activeReporter = new NoopErrorReporter();
  }

  // Set default tags
  errorReporter.setTag('environment', environment);
  if (config?.release) {
    errorReporter.setTag('release', config.release);
  }

  // Add initialization breadcrumb
  errorReporter.addBreadcrumb('Error reporting initialized', 'system', {
    environment,
    hasDsn: !!config?.dsn,
  });
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Create an AppError from a caught exception
 * Shorthand for classifyError with automatic context
 */
export function createAppError(
  error: unknown,
  type?: ErrorType,
  context?: Record<string, unknown>
): AppError {
  const appError = classifyError(error, type);
  if (context) {
    appError.context = { ...appError.context, ...context };
  }
  return appError;
}

/**
 * Report an error with optional context
 * Combines classification and reporting
 */
export function reportError(
  error: unknown,
  type?: ErrorType,
  context?: Record<string, unknown>
): AppError {
  const appError = createAppError(error, type, context);
  errorReporter.captureError(appError);
  return appError;
}

/**
 * Wrap an async function with error reporting
 */
export function withErrorReporting<T extends (...args: unknown[]) => Promise<unknown>>(
  fn: T,
  errorType?: ErrorType,
  context?: Record<string, unknown>,
  options?: { includeArgs?: boolean }
): T {
  const isDev =
    typeof process !== 'undefined'
      ? process.env.NODE_ENV === 'development'
      : import.meta.env?.DEV ?? false;

  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args);
    } catch (error) {
      // Only include args in development to avoid logging sensitive data in production
      const safeContext = { ...context };
      if (isDev || options?.includeArgs) {
        safeContext.args = args;
      }
      reportError(error, errorType, safeContext);
      throw error;
    }
  }) as T;
}

/**
 * Format an error for display in UI
 */
export function formatErrorForDisplay(error: AppError): {
  title: string;
  message: string;
  details: string;
  action: string;
  strategy: RecoveryStrategy;
} {
  const strategy = getRecoveryStrategy(error);

  return {
    title: getErrorTitle(error.type),
    message: getErrorMessage(error),
    details: error.originalError?.message ?? error.message,
    action: getRecoveryActionText(strategy),
    strategy,
  };
}

/**
 * Get a short title for an error type
 */
function getErrorTitle(type: ErrorType): string {
  switch (type) {
    case ErrorType.ANALYSIS_ERROR:
      return 'Analysis Failed';
    case ErrorType.WORKER_ERROR:
      return 'Worker Error';
    case ErrorType.NETWORK_ERROR:
      return 'Connection Error';
    case ErrorType.VALIDATION_ERROR:
      return 'Invalid Input';
    case ErrorType.MODEL_ERROR:
      return 'Model Error';
    case ErrorType.STORAGE_ERROR:
      return 'Storage Error';
    case ErrorType.RENDER_ERROR:
      return 'Display Error';
    case ErrorType.UNKNOWN_ERROR:
    default:
      return 'Something Went Wrong';
  }
}

// ============================================================================
// Exports
// ============================================================================

export type {
  Breadcrumb,
};
